import { BottomSheet, Flex, Shape, styled } from '@teko/ds-mobile';
import { LogoLoading, SelectOption } from '@teko/ecommerce';
import {
  BlockHeader,
  Button,
  ListItem,
  ProductCardType,
  Text,
} from '@teko/ecommerce/mobile';
import { PriceFilter } from '@teko/teko-ecommerce';
import { Filter as FilterInterface, FilterOption } from '@teko/types/ecommerce';
import { Divider, Grid, SvgIcon, useTheme } from '@teko/ui';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SortOptionInterface } from '../../../../modules/search-page/shared';
import {
  getDefaultSortOptions,
  getDisplayFilter,
} from '../../../../modules/search-page/shared/helpers';

interface IFilterPrice {
  max: number;
  min: number;
  step: number;
  value: {
    gte: number;
    lte: number;
  };
}

interface BuildPCFilterMobileProps {
  onClickLayout: () => void;
  layoutType: ProductCardType;
  filters: FilterInterface[];
  loading?: boolean;
  filterPrice: IFilterPrice;
  activeOptions: FilterOption[];
  activeSortOption?: SortOptionInterface;
  onSortOptionSelected: (sortOption: SortOptionInterface) => void;
  onSubmit: (
    activeFilters: FilterOption[],
    filterPriceValue: {
      gte: number;
      lte: number;
    },
  ) => void;
  onClearFilters: () => void;
}

export const BuildPCFilterMobile: React.FC<BuildPCFilterMobileProps> = ({
  onClickLayout,
  layoutType,
  filters,
  loading = false,
  filterPrice,
  activeOptions,
  activeSortOption,
  onSortOptionSelected,
  onSubmit,
  onClearFilters,
}: BuildPCFilterMobileProps) => {
  const { brand: theme } = useTheme();
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const { sortByCreatedAt } = useTenantConfig();

  const [priceFilter, setPriceFilter] = useState<{ gte: number; lte: number }>(
    filterPrice.value,
  );

  const [filterActiveOptions, setFilterActiveOptions] =
    useState<FilterOption[]>(activeOptions);

  useEffect(() => {
    setFilterActiveOptions(activeOptions);
  }, [activeOptions]);

  const onClickFilterOption = (option: FilterOption) => {
    const optionIndex = filterActiveOptions.findIndex(
      (opt) =>
        opt.filterCode === option.filterCode &&
        opt.filterId === option.filterId &&
        opt.optionId === option.optionId,
    );
    if (optionIndex > -1) {
      setFilterActiveOptions((prev) =>
        prev.filter((_, index) => index !== optionIndex),
      );
    } else {
      setFilterActiveOptions((prev) => [...prev, option]);
    }
  };

  const isOptionSelected = (
    id?: string | number | null,
    name?: string | null,
  ) => {
    return !!filterActiveOptions.find(
      (option) => option.optionId === id && option.optionName === name,
    );
  };

  // render sort bar
  const renderSortBar = () => {
    return (
      <SortBarBottomSheet
        zIndex={1010}
        position="top"
        show={showSort}
        closeBottomSheet={() => setShowSort(false)}
      >
        <Shape>
          {getDefaultSortOptions(sortByCreatedAt).map(
            (option, index: number) => (
              <React.Fragment key={index}>
                <ListItem
                  onClick={() => {
                    onSortOptionSelected(option);
                    setShowSort(false);
                  }}
                  title={
                    <Text
                      color={
                        activeSortOption?.optionCode === option.optionCode
                          ? 'primary'
                          : ''
                      }
                      fontSize={13}
                    >
                      {t(option.optionName)}
                    </Text>
                  }
                  right={
                    activeSortOption?.optionCode === option.optionCode && (
                      <SvgIcon color="primary" name="check" size={10} />
                    )
                  }
                />
                <Divider />
              </React.Fragment>
            ),
          )}
        </Shape>
      </SortBarBottomSheet>
    );
  };

  // render filter options excluding price filter
  const renderFilterOptions = (): React.ReactNode => {
    const displayFilters = getDisplayFilter(filters);
    if (!displayFilters.length) return null;
    return displayFilters.map((attribute, index: number) => (
      <FilterBlock
        key={index}
        attribute={attribute}
        isOptionSelected={isOptionSelected}
        onSelectOption={onClickFilterOption}
      />
    ));
  };

  return (
    <>
      <StyledFlex align="center" justify="space-between">
        <Flex onClick={() => setShowSort(!showSort)} align="center">
          <Text fontSize={13} fontWeight="500">
            {(activeSortOption && t(activeSortOption.optionName)) ||
              t('Sắp xếp')}
          </Text>
          <SvgIcon marginLeft="4px" color="#333" size={8} name="caret-down" />
        </Flex>

        <Flex align="center">
          {layoutType && (
            <Flex align="center" justify="center">
              <SvgIcon
                onClick={onClickLayout}
                name={layoutType === 'grid' ? 'grid' : 'list-layout'}
                color="#333"
                size={16}
              />
              <DividerVertical />
            </Flex>
          )}
          <Flex
            onClick={() => setShowFilter(true)}
            align="center"
            justify="center"
          >
            <Text fontSize={13} fontWeight="500">
              {t('Bộ lọc')}
            </Text>
            <SvgIcon
              marginLeft="2px"
              color={
                filterActiveOptions.length ||
                filterPrice.value.gte > filterPrice.min ||
                filterPrice.value.lte < filterPrice.max
                  ? theme.primary
                  : '#333'
              }
              size={18}
              name="filter"
            />
          </Flex>
        </Flex>
      </StyledFlex>
      {renderSortBar()}
      <FilterBottomSheet
        contentHeight="100%"
        position="right"
        show={showFilter}
        closeBottomSheet={() => setShowFilter(false)}
        zIndex={1051}
      >
        <FilterContent>
          <FilterOptions>
            {loading ? (
              <StyledLoadingFilter />
            ) : (
              <>
                <BlockHeader
                  title={{
                    fontSize: 14,
                    fontWeight: '500',
                  }}
                >
                  {t('Khoảng giá (đ)')}
                </BlockHeader>
                <PriceFilter
                  {...filterPrice}
                  onFilter={(gte, lte) => setPriceFilter({ gte, lte })}
                  paddingBottom="s8"
                  paddingLeft="s40"
                  paddingRight="s40"
                />
                <Divider />
                {renderFilterOptions()}
              </>
            )}
          </FilterOptions>
          <ActionBar>
            <Flex width="100%" align="center" justify="end">
              <Button
                onClick={() => {
                  setFilterActiveOptions([]);
                  onClearFilters();
                  setShowFilter(false);
                }}
                variant="primary"
                outline
                size="sm"
              >
                {t('Xóa bộ lọc')}
              </Button>
              <Button
                onClick={() => {
                  onSubmit(filterActiveOptions, priceFilter);
                  setShowFilter(false);
                }}
                marginLeft="8px"
                size="sm"
              >
                {t('Áp dụng')}
              </Button>
            </Flex>
          </ActionBar>
        </FilterContent>
      </FilterBottomSheet>
    </>
  );
};

const FilterBlock = ({
  attribute,
  onSelectOption,
  isOptionSelected,
}: {
  attribute: FilterInterface;
  onSelectOption: (option: FilterOption) => void;
  isOptionSelected: (
    id?: string | number | null,
    name?: string | null,
  ) => boolean;
}) => {
  const [showAll, setShowAll] = useState(false);
  const options = attribute.options;
  const blockRef = useRef<HTMLDivElement>(null);
  const blockWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (blockRef.current && blockWrapperRef.current) {
      if (showAll) {
        blockWrapperRef.current.style.maxHeight =
          blockRef.current.clientHeight + 'px';
      } else {
        blockWrapperRef.current.style.maxHeight = '10rem';
      }
    }
  }, [showAll]);

  if (!options?.length) return null;

  return (
    <React.Fragment>
      <BlockHeader
        title={{
          fontSize: 14,
          fontWeight: '500',
        }}
      >
        {attribute.name}
      </BlockHeader>
      <StyleBlockWrapper ref={blockWrapperRef}>
        <div ref={blockRef}>
          <StyledGrid paddingBottom="6px" paddingX="12px" colNum={2}>
            {options.map((value, index: number) => (
              <StyledOption
                onClickOption={() => onSelectOption(value)}
                isActive={isOptionSelected(value.optionId, value.optionName)}
                key={index}
                stickySize="sm"
              >
                <Text
                  textAlign="center"
                  color={
                    isOptionSelected(value.optionId, value.optionName)
                      ? 'primary'
                      : '#333'
                  }
                  fontSize={13}
                  maxLines={1}
                >
                  {value.optionName}
                </Text>
              </StyledOption>
            ))}
          </StyledGrid>
        </div>
      </StyleBlockWrapper>
      {options.length > 8 && (
        <Flex justify="center" paddingBottom={'s4'}>
          <Button variant="link" onClick={() => setShowAll(!showAll)}>
            {!showAll ? t('Xem thêm') : 'Ẩn bớt'}
          </Button>
        </Flex>
      )}
      <Divider />
    </React.Fragment>
  );
};

const SortBarBottomSheet = styled(BottomSheet)`
  position: absolute;
  width: 100%;
  top: 150px;
  bottom: unset;
`;

const StyleBlockWrapper = styled.div`
  -moz-transition: all 0.25s;
  -ms-transition: all 0.25s;
  -o-transition: all 0.25s;
  -webkit-transition: all 0.25s;
  transition: all 0.25s ease-in-out;
  overflow: hidden;
  max-height: '10rem';
`;

const StyledOption = styled(SelectOption)`
  padding: 5px;
  margin: 0px 4px 8px 4px;
  display: block;
`;

const StyledGrid = styled(Grid)`
  transition: 'all 0.25s ease-in-out';
`;

const FilterBottomSheet = styled(BottomSheet)`
  width: 85%;
  right: 0;
  top: 0;
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
  border-top-right-radius: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledFlex = styled(Flex)`
  background: #fff;
  padding: 0 16px;
  position: sticky;
  top: 40px;
  z-index: 1040;
  height: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.background};
`;

const FilterContent = styled(Shape)`
  height: 100%;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
`;

const ActionBar = styled(Flex)`
  padding: 12px 16px;
  background: #fff;
  text-align: center;
  z-index: 1200;
`;

const FilterOptions = styled(Shape)`
  height: 100%;
  max-height: 100%;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const StyledLoadingFilter = styled(LogoLoading)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  margin: auto;
`;

const DividerVertical = styled.span`
  border-right: 0.5px solid #ddd;
  height: 16px;
  margin: 0 8px;
`;
